/**
 * RecordDetailBody - one fetched record in full (HIU_FLOW_107-113): readable summary,
 * attachments and the raw-data toggle. Used by the records view (HiuConsentRecordsPage).
 *
 * Dispatches on the record's top-level hiType field (never inferred from parsedSummary's
 * shape). Unmapped/unknown types fall back to GenericRenderer - never a blank screen.
 */
import React, { useState } from 'react';

import { downloadAttachment } from '../../services/api/hiuApi';
import useFileViewerStore from '../../stores/useFileViewerStore';
import GenericRenderer from './renderers/GenericRenderer';
import RawBundleView from './RawBundleView';
import ClinicalRecordRenderer from './renderers/ClinicalRecordRenderer';

// Every HI type mapped in hi_type_codes.py (all eight) resolves to the same parsedSummary
// shape (bundle_parser.py's parse_bundle() doesn't branch by hi_type), so they all share one
// renderer (Invoice adds its "Bill" section from parsedSummary.invoices). Only a type the
// backend can't classify ("Unknown") falls through to GenericRenderer below.
const RENDERER_REGISTRY = {
  OPConsultation: ClinicalRecordRenderer,
  Prescription: ClinicalRecordRenderer,
  DiagnosticReport: ClinicalRecordRenderer,
  DischargeSummary: ClinicalRecordRenderer,
  Invoice: ClinicalRecordRenderer,
  ImmunizationRecord: ClinicalRecordRenderer,
  HealthDocumentRecord: ClinicalRecordRenderer,
  WellnessRecord: ClinicalRecordRenderer,
};

/**
 * AttachmentsSection — F7. Container-owned attachment handling (not inside individual
 * HI-type renderers, since it's orthogonal to hi_type-specific rendering — mirrors
 * LabReportActions.jsx's container-owns-the-fetch pattern).
 *
 * Gated on hasData && id: an entry missing either can't ever be downloaded (a
 * metadata-only or url-only DocumentReference — this phase never fetches
 * attachment.url), so it renders as plain non-actionable text, never a dead-end button.
 */
function AttachmentsSection({ fetchId, recordId, documentReferences, isDark }) {
  const [errorByDocId, setErrorByDocId] = useState({});
  const [loadingDocId, setLoadingDocId] = useState(null);

  if (!documentReferences || documentReferences.length === 0) return null;

  const handleOpen = async (doc) => {
    setErrorByDocId((prev) => ({ ...prev, [doc.id]: '' }));
    setLoadingDocId(doc.id);
    try {
      const { blobUrl, contentType } = await downloadAttachment(fetchId, recordId, doc.id);
      const fileType = contentType === 'application/pdf' ? 'pdf'
        : contentType?.startsWith('image/') ? 'image'
        : 'unknown'; // FileViewerModal's own existing generic-download fallback
      useFileViewerStore.getState().openViewer({ blobUrl, fileType, title: doc.title || 'Document' });
    } catch (err) {
      // Backend rejection (404/422/413/500) shows inline, never opens the viewer with
      // a stale/invalid blob URL.
      setErrorByDocId((prev) => ({ ...prev, [doc.id]: err.userMessage || 'Could not open attachment.' }));
    } finally {
      setLoadingDocId(null);
    }
  };

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}` }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.03em', color: isDark ? '#9ca3af' : '#6b7280', marginBottom: 6 }}>
        ATTACHMENTS
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {documentReferences.map((doc) => {
          const label = doc.title || 'Document';
          const actionable = doc.hasData && doc.id;
          return (
            <div key={doc.id || label}>
              {actionable ? (
                <button
                  onClick={() => handleOpen(doc)}
                  disabled={loadingDocId === doc.id}
                  className="px-3 py-1.5 rounded-md border text-sm"
                  style={{
                    border: `1px solid ${isDark ? '#475569' : '#94a3b8'}`,
                    background: isDark ? '#1e293b' : '#ffffff',
                    color: isDark ? '#f1f5f9' : '#1e293b',
                    cursor: loadingDocId === doc.id ? 'wait' : 'pointer',
                    opacity: loadingDocId === doc.id ? 0.6 : 1,
                  }}
                >
                  📄 {loadingDocId === doc.id ? 'Opening…' : label}
                </button>
              ) : (
                <span style={{ fontSize: 13, color: isDark ? '#9ca3af' : '#6b7280' }}>📄 {label}</span>
              )}
              {errorByDocId[doc.id] && (
                <p style={{ color: isDark ? '#f87171' : '#dc2626', fontSize: 12, margin: '4px 0 0' }}>
                  {errorByDocId[doc.id]}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function RecordDetailBody({ record, fetchId, isDark }) {
  const Renderer = RENDERER_REGISTRY[record.hiType] || GenericRenderer;
  return (
    <div>
      <Renderer parsedSummary={record.parsedSummary} isDark={isDark} />
      <AttachmentsSection
        fetchId={fetchId}
        recordId={record.recordId}
        documentReferences={record.documentReferences}
        isDark={isDark}
      />
      <RawBundleView fetchId={fetchId} recordId={record.recordId} isDark={isDark} />
    </div>
  );
}
